import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  alpha,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Link,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import NavigateNextRoundedIcon from '@mui/icons-material/NavigateNextRounded';

export const mediaBaseUrl = 'https://aaryanholidays.in/express/uploads/';

export const truncateText = (value, limit = 90) => {
  if (!value) {
    return '-';
  }

  return value.length > limit ? `${value.slice(0, limit)}...` : value;
};

export const FlowBreadcrumbs = ({ items = [] }) => (
  <Breadcrumbs
    separator={<NavigateNextRoundedIcon fontSize="small" />}
    aria-label="breadcrumb"
    sx={{ mb: 2.5, color: '#64748b' }}
  >
    {items.map((item, index) => {
      const isLast = index === items.length - 1;

      if (!item.to || isLast) {
        return (
          <Typography key={`${item.label}-${index}`} color="text.primary" sx={{ fontWeight: 700 }}>
            {item.label}
          </Typography>
        );
      }

      return (
        <Link
          key={`${item.label}-${index}`}
          component={RouterLink}
          to={item.to}
          underline="hover"
          color="inherit"
          sx={{ fontWeight: 600 }}
        >
          {item.label}
        </Link>
      );
    })}
  </Breadcrumbs>
);

export const FlowPageHeader = ({ title, subtitle, action, search, searchPlaceholder = 'Search', onSearchChange }) => (
  <Card
    sx={{
      mb: 3,
      borderRadius: 5,
      border: '1px solid rgba(148, 163, 184, 0.16)',
      boxShadow: '0 20px 50px rgba(15, 23, 42, 0.08)'
    }}
  >
    <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
      <Stack spacing={2.5}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', md: 'center' }}
        >
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', mb: 0.75 }}>
              {title}
            </Typography>
            {subtitle ? (
              <Typography variant="body1" sx={{ color: '#64748b', maxWidth: 860 }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>
          {action ? action : null}
        </Stack>
        {typeof search === 'string' && onSearchChange ? (
          <TextField
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder={searchPlaceholder}
            fullWidth
            size="medium"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3
              }
            }}
          />
        ) : null}
      </Stack>
    </CardContent>
  </Card>
);

export const FlowStatGrid = ({ items = [] }) => (
  <Grid container spacing={2} sx={{ mb: 3 }}>
    {items.map((item) => {
      const Icon = item.icon;

      return (
        <Grid item xs={12} sm={6} md={4} key={item.label}>
          <Card
            sx={{
              borderRadius: 4,
              border: '1px solid rgba(148, 163, 184, 0.16)',
              boxShadow: '0 12px 30px rgba(15, 23, 42, 0.06)'
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: 3,
                    display: 'grid',
                    placeItems: 'center',
                    background: item.tone || 'linear-gradient(135deg, #1d4ed8 0%, #38bdf8 100%)',
                    color: '#ffffff',
                    boxShadow: '0 16px 30px rgba(37, 99, 235, 0.18)'
                  }}
                >
                  {Icon ? <Icon /> : null}
                </Box>
                <Box>
                  <Typography variant="overline" sx={{ color: '#64748b', letterSpacing: '0.1em' }}>
                    {item.label}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>
                    {item.value}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      );
    })}
  </Grid>
);

export const FlowFormCard = ({ title, subtitle, children }) => (
  <Card
    sx={{
      borderRadius: 5,
      border: '1px solid rgba(148, 163, 184, 0.16)',
      boxShadow: '0 20px 50px rgba(15, 23, 42, 0.08)'
    }}
  >
    <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', mb: 0.75 }}>
            {title}
          </Typography>
          {subtitle ? (
            <Typography variant="body1" sx={{ color: '#64748b', maxWidth: 840 }}>
              {subtitle}
            </Typography>
          ) : null}
        </Box>
        {children}
      </Stack>
    </CardContent>
  </Card>
);

export const SubmitButton = ({ loading, children, ...props }) => (
  <Button
    {...props}
    disabled={loading || props.disabled}
    startIcon={loading ? <CircularProgress size={18} color="inherit" /> : props.startIcon}
    sx={{
      borderRadius: 999,
      px: 3,
      py: 1.3,
      textTransform: 'none',
      fontWeight: 700,
      ...(props.sx || {})
    }}
  >
    {loading ? 'Saving...' : children}
  </Button>
);

export const SoftPanel = ({ children, sx = {} }) => (
  <Card
    sx={{
      borderRadius: 5,
      border: '1px solid rgba(148, 163, 184, 0.16)',
      boxShadow: '0 20px 50px rgba(15, 23, 42, 0.08)',
      ...sx
    }}
  >
    <CardContent sx={{ p: { xs: 2, md: 3 } }}>{children}</CardContent>
  </Card>
);

export const mediaPreviewStyles = {
  width: '100%',
  maxWidth: 240,
  maxHeight: 180,
  objectFit: 'cover',
  borderRadius: 16,
  border: '1px solid rgba(148, 163, 184, 0.16)',
  backgroundColor: alpha('#e2e8f0', 0.5)
};
